import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../../modules/auth'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { searchDefects } from '../../../modules/QHSE/core/_requests'
import type { QHSEDefectDto } from '../../../modules/QHSE/core/_models'
import { getVoyageList } from '../../../modules/operations/core/_requests'
import type { Voyage } from '../../../modules/operations/core/_models'
import { getPmsOverdueJobs, getPmsCalendarEvents } from '../core/_requests'
import { isBroadFleetPickerUser, getUserCompanyGroupAdminId } from '../../../modules/QHSE/core/qhseUserContext'
import { QHSE_PERMS } from '../../../modules/QHSE/core/permissions'
import { TECH_PERM } from '../../../modules/Technical/core/technicalPermissions'
import { OP } from '../../../modules/operations/core/operationPermissions'
import type {
  HomeDashboardData, DashboardKpi, RiskItem, WorkItem,
  TimelineEvent, InsightItem, PortCall, CriticalEvent, FleetHealthData,
} from '../core/dashboard-types'

/* ─── helpers ─── */
const resolve = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
  result.status === 'fulfilled' ? result.value : fallback

/** Derive a severity level from defect status + age. QHSEDefectDto has no raw severity field. */
const defectSeverityLabel = (d: QHSEDefectDto): 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' => {
  if (d.status === 'SUBMIT') return 'CRITICAL'
  if (d.status === 'SAVE') {
    if (d.dateObserved) {
      const days = (Date.now() - new Date(d.dateObserved).getTime()) / 86400000
      if (days > 30) return 'HIGH'
      if (days > 14) return 'MEDIUM'
    }
    return 'LOW'
  }
  return 'LOW'
}

/** Numeric score for sorting — higher = more severe */
const severityScore = (d: QHSEDefectDto): number => {
  const lbl = defectSeverityLabel(d)
  if (lbl === 'CRITICAL') return 4
  if (lbl === 'HIGH') return 3
  if (lbl === 'MEDIUM') return 2
  return 1
}

const formatDueLabel = (dateStr?: string | null): string => {
  if (!dateStr) return ''
  const diff = new Date(dateStr).getTime() - Date.now()
  if (diff <= 0) return 'Overdue'
  const hours = Math.round(diff / 3_600_000)
  if (hours < 24) return `Due in ${hours} hr${hours === 1 ? '' : 's'}`
  const days = Math.round(hours / 24)
  return `Due in ${days} day${days === 1 ? '' : 's'}`
}

const eventColor = (eventType?: string): 'red' | 'emerald' | 'purple' | 'orange' | 'blue' | 'teal' => {
  const t = (eventType || '').toLowerCase()
  if (t.includes('maintenance') || t.includes('overhaul')) return 'red'
  if (t.includes('crew')) return 'emerald'
  if (t.includes('port') || t.includes('bunker')) return 'purple'
  if (t.includes('dock') || t.includes('dry')) return 'orange'
  if (t.includes('inspection') || t.includes('audit')) return 'blue'
  return 'teal'
}

const kalmanTotalVessels = 58

/* ─────────────────────────────────────────────────────────
   HOOK
   ───────────────────────────────────────────────────────── */

export function useHomeDashboardData(): HomeDashboardData {
  const { currentUser, hasPermission } = useAuth()
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()

  const broadFleet = isBroadFleetPickerUser(currentUser)
  const myCgaId = getUserCompanyGroupAdminId(currentUser)

  /* ── Loading / error state ── */
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* ── Real API data state ── */
  const [defects, setDefects] = useState<QHSEDefectDto[]>([])
  const [overdueJobs, setOverdueJobs] = useState<any[]>([])
  const [calendarEvents, setCalendarEvents] = useState<any[]>([])
  const [voyages, setVoyages] = useState<Voyage[]>([])

  /* ── Effective company scope (mirrors OperationsDashboard pattern) ── */
  const effectiveCgaId = useMemo(() => {
    if (globalState.company?.type === 'company_group') return globalState.company.id
    const vessel = globalVessels.find((v: any) =>
      v.companyAdmin?.id === globalState.company?.id
    )
    if (vessel) return vessel.companyGroupAdmin?.id
    return broadFleet ? undefined : (myCgaId ?? undefined)
  }, [globalState.company, globalVessels, broadFleet, myCgaId])

  /* ── Fetch all data in parallel ── */
  const fetchAll = useCallback(async () => {
    setLoaded(false)
    setError(null)
    try {
      const results = await Promise.allSettled([
        // 1) QHSE defects — for KPI bar, risks, insights, health donut
        hasPermission(QHSE_PERMS.DEFECT_READ)
          ? searchDefects({ activeOnly: true, companyGroupId: effectiveCgaId })
          : Promise.resolve([] as QHSEDefectDto[]),

        // 2) PMS overdue jobs — for work queue, insights
        hasPermission(TECH_PERM.READ_PMS_JOB)
          ? getPmsOverdueJobs(effectiveCgaId)
          : Promise.resolve([]),

        // 3) PMS calendar events — for timeline, critical events
        hasPermission(TECH_PERM.READ_PMS_CALENDAR)
          ? getPmsCalendarEvents(effectiveCgaId)
          : Promise.resolve([]),

        // 4) Voyage list — for port calls
        hasPermission(OP.READ_VOYAGE)
          ? getVoyageList()
          : Promise.resolve([]),
      ])

      setDefects(resolve(results[0], []))
      setOverdueJobs(resolve(results[1], []))
      setCalendarEvents(resolve(results[2], []))
      setVoyages(resolve(results[3], []))
    } catch (e: any) {
      setError(e?.message || 'Failed to load dashboard data')
    } finally {
      setLoaded(true)
    }
  }, [effectiveCgaId, hasPermission])

  useEffect(() => { fetchAll() }, [fetchAll])

  /* ════════════════════════════════════════════
     DERIVED KPIs (useMemo, re-compute on data change)
     ════════════════════════════════════════════ */

  // --- KPI tile counts ---
  const kpis = useMemo<DashboardKpi[]>(() => {
    const criticalCount = defects.filter(
      d => d.status === 'SUBMIT'
    ).length

    const highCount = defects.filter(
      d => d.status === 'SAVE' && d.dateObserved && (Date.now() - new Date(d.dateObserved).getTime()) / 86400000 > 30
    ).length

    const mediumCount = defects.filter(
      d => d.status !== 'CLOSE' && d.status !== 'REVIEWED'
    ).length + overdueJobs.length

    const healthScore = Math.max(0, Math.min(100, 100 - (criticalCount * 5 + highCount * 2)))

    return [
      { label: 'Global Risks', value: criticalCount, icon: 'risks' as const, color: 'red' as const, subtitle: 'Require immediate attention' },
      { label: 'Warnings', value: highCount, icon: 'warnings' as const, color: 'amber' as const, subtitle: 'Require action soon' },
      { label: 'Informational', value: mediumCount, icon: 'info' as const, color: 'blue' as const, subtitle: 'For monitoring' },
      { label: 'Fleet Health', value: healthScore, icon: 'health' as const, color: 'emerald' as const, subtitle: 'Overall fleet health' },
    ]
  }, [defects, overdueJobs])

  // --- Risk items (top 5 by severity then oldest observed) ---
  const risks = useMemo<RiskItem[]>(() => {
    return defects
      .filter(d => d.status !== 'CLOSE' && d.status !== 'REVIEWED')
      .sort((a, b) => {
        const sDiff = severityScore(b) - severityScore(a)
        if (sDiff !== 0) return sDiff
        const dA = a.dateObserved ? new Date(a.dateObserved).getTime() : 0
        const dB = b.dateObserved ? new Date(b.dateObserved).getTime() : 0
        return dA - dB
      })
      .slice(0, 5)
      .map(d => ({
        id: d.id ?? 0,
        severity: defectSeverityLabel(d),
        description: d.displayTitle || d.description || d.defectNumber || 'Untitled',
        impact: d.equipmentName || (d.dacActionName ?? ''),
        vessel: d.vesselName || '',
      }))
  }, [defects])

  // --- Work queue (merge PMS overdue jobs + open defects, sorted by priority then due) ---
  const workQueue = useMemo<WorkItem[]>(() => {
    const fromDefects: WorkItem[] = defects
      .filter(d => d.status === 'SUBMIT')
      .slice(0, 6)
      .map(d => ({
        id: `d-${d.id}`,
        type: 'review' as const,
        description: d.displayTitle || d.description || 'Open defect',
        vessel: d.vesselName || '',
        priority: defectSeverityLabel(d) === 'CRITICAL' || defectSeverityLabel(d) === 'HIGH'
          ? 'HIGH' as const : 'MEDIUM' as const,
        dueLabel: formatDueLabel(d.dateObserved),
        actionLabel: 'Review',
      }))

    const fromPms: WorkItem[] = overdueJobs.slice(0, 6).map((job: any) => ({
      id: `p-${job.id}`,
      type: (job.jobType === 'MAINTENANCE' ? 'plan' : job.jobType === 'INSPECTION' ? 'review' : 'action') as 'approve' | 'assign' | 'plan' | 'review' | 'action',
      description: job.title || job.taskDescription || 'Overdue PMS job',
      vessel: job.vesselName || '',
      priority: (job.criticality === 'CRITICAL' || job.criticality === 'HIGH' ? 'HIGH' : job.criticality === 'MEDIUM' ? 'MEDIUM' : 'LOW') as 'HIGH' | 'MEDIUM' | 'LOW',
      dueLabel: formatDueLabel(job.dueDate),
      actionLabel: job.status === 'OVERDUE' ? 'Review' : 'Action',
    }))

    // Merge, sort: HIGH first, then by due date
    return [...fromDefects, ...fromPms]
      .sort((a, b) => {
        const pa = a.priority === 'HIGH' ? 0 : a.priority === 'MEDIUM' ? 1 : 2
        const pb = b.priority === 'HIGH' ? 0 : b.priority === 'MEDIUM' ? 1 : 2
        return pa - pb
      })
      .slice(0, 8)
  }, [overdueJobs, defects])

  // --- Timeline (7-day scheduler from calendar events) ---
  const timeline = useMemo<TimelineEvent[]>(() => {
    const now = Date.now()
    const sevenDays = 7 * 24 * 60 * 60 * 1000
    const rangeEnd = now + sevenDays

    return calendarEvents
      .filter((evt: any) => {
        const start = evt.startDate ? new Date(evt.startDate).getTime() : 0
        return start >= now && start <= rangeEnd
      })
      .slice(0, 8)
      .map((evt: any, _idx: number) => {
        const start = evt.startDate ? new Date(evt.startDate).getTime() : now
        const end = evt.endDate ? new Date(evt.endDate).getTime() : start + 86400000
        const leftPct = Math.max(0, ((start - now) / sevenDays) * 100)
        const widthPct = Math.min(100 - leftPct, Math.max(4, ((end - start) / sevenDays) * 100))
        return {
          id: evt.id ?? _idx,
          vessel: evt.vesselName || '',
          label: evt.title || evt.taskDescription || 'Event',
          color: eventColor(evt.eventType),
          leftPct: Math.round(leftPct),
          widthPct: Math.round(widthPct),
        }
      })
  }, [calendarEvents])

  // --- Insights ---
  const insights = useMemo<InsightItem[]>(() => {
    const result: InsightItem[] = []

    // Insight 1: overdue PMS jobs count
    if (overdueJobs.length > 0) {
      result.push({
        id: 'i-overdue',
        severity: 'orange',
        icon: 'alert-circle',
        message: '',
        highlight: `${overdueJobs.length} PMS jobs overdue`,
      })
    }

    // Insight 2: unclosed defects that are 30+ days old
    const oldDefects = defects.filter(d => {
      if (d.status === 'CLOSE' || d.status === 'REVIEWED') return false
      if (!d.dateObserved) return false
      const days = (Date.now() - new Date(d.dateObserved).getTime()) / 86400000
      return days > 30
    })
    const uniqueVesselsOld = new Set(oldDefects.map(d => d.vesselName)).size
    if (uniqueVesselsOld > 0) {
      result.push({
        id: 'i-old-defects',
        severity: 'red',
        icon: 'clock',
        message: '',
        highlight: `${uniqueVesselsOld} vessel${uniqueVesselsOld > 1 ? 's' : ''} with overdue audits`,
      })
    }

    // Insight 3: active voyages count
    const activeVoyages = voyages.filter(v => v.active).length
    if (activeVoyages > 0) {
      result.push({
        id: 'i-voyages',
        severity: 'blue',
        icon: 'navigation',
        message: '',
        highlight: `${activeVoyages} active voyage${activeVoyages > 1 ? 's' : ''} in progress`,
      })
    }

    return result.slice(0, 4)
  }, [overdueJobs, defects, voyages])

  // --- Port calls ---
  const portCalls = useMemo<PortCall[]>(() => {
    const now = new Date()
    return voyages
      .filter(v => {
        if (!v.endDate) return false
        return new Date(v.endDate) >= now
      })
      .slice(0, 6)
      .map(v => ({
        id: v.id,
        vessel: v.vessel?.fleet_name || '',
        port: v.arrivalPort || '',
        eta: v.endDate ? new Date(v.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '',
        purpose: v.loadingCondition || 'Port Call',
      }))
  }, [voyages])

  // --- Fleet health donut ---
  const health = useMemo<FleetHealthData>(() => {
    const criticalCount = defects.filter(d => d.status === 'SUBMIT').length
    const highCount = defects.filter(d => d.status === 'SAVE' && d.dateObserved && (Date.now() - new Date(d.dateObserved).getTime()) / 86400000 > 30).length
    const medCount = defects.filter(d => d.status !== 'CLOSE' && d.status !== 'REVIEWED').length
    const goodCount = Math.max(0, kalmanTotalVessels - (criticalCount + highCount + medCount))

    const pct = Math.max(0, Math.min(100, 100 - (criticalCount * 5 + highCount * 2)))
    const label = pct >= 90 ? 'Good' : pct >= 70 ? 'Fair' : 'Needs Attention'

    return {
      percentage: pct,
      label,
      items: [
        { label: 'Critical Systems Overdue', color: 'red' as const, count: criticalCount },
        { label: 'High Priority Jobs Overdue', color: 'amber' as const, count: highCount },
        { label: 'Equipment w/ Degradation', color: 'slate' as const, count: medCount },
        { label: 'Vessels in Good Health', color: 'emerald' as const, count: goodCount },
      ],
    }
  }, [defects])

  // --- Critical events ---
  const criticalEvents = useMemo<CriticalEvent[]>(() => {
    const now = Date.now()
    const fourteenDays = 14 * 24 * 60 * 60 * 1000
    return calendarEvents
      .filter((evt: any) => {
        if (!evt.startDate) return false
        const start = new Date(evt.startDate).getTime()
        return start >= now && start <= now + fourteenDays
      })
      .sort((a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .slice(0, 4)
      .map((evt: any) => {
        const start = new Date(evt.startDate)
        const day = start.getDate().toString().padStart(2, '0')
        const month = start.toLocaleString('en-US', { month: 'short' }).toUpperCase()
        const diffDays = Math.ceil((start.getTime() - now) / 86400000)
        return {
          id: evt.id ?? 0,
          day,
          month,
          title: evt.title || evt.taskDescription || 'Critical Event',
          vessel: evt.vesselName || '',
          daysLabel: diffDays <= 0 ? 'Today' : diffDays === 1 ? 'Tomorrow' : `in ${diffDays} days`,
          severity: (evt.criticality === 'HIGH' || evt.criticality === 'CRITICAL' ? 'HIGH' : 'MEDIUM') as 'HIGH' | 'MEDIUM',
        }
      })
  }, [calendarEvents])

  /* ── Build return value ── */
  return {
    kpis,
    risks,
    workQueue,
    timeline,
    insights,
    portCalls,
    health,
    criticalEvents,
    rawDefects: defects,
    rawOverdueJobs: overdueJobs,
    rawVoyages: voyages,
    loaded,
    error,
    refetch: fetchAll,
  }
}
